
import { sanitizeErrorMessage, isNetworkErrorMessage, isGenericUserMessage } from "./secureErrorHandler";
import { normalizeError, type ErrorWithMessage } from "@/hooks/useErrorHandler";
import { getCachedUserId } from "@/services/auth/sessionCache";
import * as Sentry from "@sentry/react";
import { APP_VERSION } from "@/version";

const isDev =
  (import.meta as any).env?.MODE === 'development' || process.env.NODE_ENV === 'development';

// ✅ Enable logs for debugging but with security considerations
const LOG_ENABLED = true;

// Secure logging levels - more restrictive in production
const LOG_LEVELS_ENABLED = {
  log: isDev,      // Only in development
  info: isDev,     // Only in development (silenzia la console in produzione)
  warn: isDev,     // Only in development (silenzia la console in produzione)
  error: true,     // Always enabled (errors)
  debug: isDev     // Only in development
};

// Sensitive keywords to filter out
const SENSITIVE_KEYWORDS = [
  'password', 'token', 'secret', 'key', 'credential',
  'auth', 'session', 'jwt', 'bearer', 'api_key'
];

type SupabaseInsertError = {
  code?: string;
  constraint?: string;
  message?: string;
  details?: string;
};

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

const isUserForeignKeyConstraintError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { code, constraint, message, details } = error as SupabaseInsertError;

  if (code !== '23503') {
    return false;
  }

  const constraintMessage = constraint || message || details;

  return typeof constraintMessage === 'string'
    ? constraintMessage.includes('log_errors_user_id_fkey')
    : false;
};

/**
 * Scrub secrets from a string WITHOUT collapsing it to a generic message.
 * Unlike sanitizeErrorMessage (used for user-facing toasts), this preserves
 * the real diagnostic content so log_errors stays useful.
 */
function scrubSensitiveString(message: string): string {
  let sanitized = message;
  SENSITIVE_KEYWORDS.forEach(keyword => {
    const regex = new RegExp(`${keyword}[=:][^\\s,}]+`, 'gi');
    sanitized = sanitized.replace(regex, `${keyword}=***`);
  });
  return sanitized;
}

/** Recursively scrub secrets from any value persisted to log_errors. */
function scrubSensitiveValue(value: any): any {
  if (typeof value === 'string') {
    return scrubSensitiveString(value);
  }
  if (value && typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value);
      const scrubbed = SENSITIVE_KEYWORDS.reduce(
        (acc, keyword) =>
          acc.replace(new RegExp(`("${keyword}"\\s*:\\s*")[^"]*"`, 'gi'), '$1***"'),
        serialized
      );
      return JSON.parse(scrubbed);
    } catch {
      return '[OBJECT_SANITIZED]';
    }
  }
  return value;
}

/** Treats null/undefined and empty arrays/objects as "no info". */
function isEmptyInfo(info: any): boolean {
  if (info === null || info === undefined) return true;
  if (Array.isArray(info)) return info.length === 0;
  if (typeof info === 'object') return Object.keys(info).length === 0;
  return false;
}

/**
 * Build a useful, scrubbed additional_info payload. Never persists empty
 * arrays/objects; falls back to a normalized error context (code/status/
 * name/stack) when the caller did not provide meaningful info.
 */
function buildAdditionalInfo(raw: any, normalized: ErrorWithMessage | null): any {
  let info: any = isEmptyInfo(raw) ? undefined : raw;

  if (info === undefined && normalized) {
    const err = normalized.details;
    const context: Record<string, unknown> = {};
    if (normalized.code != null) context.code = normalized.code;
    if (normalized.status != null) context.status = normalized.status;
    if (err instanceof Error) {
      context.name = err.name;
      if (err.stack) {
        context.stackSnippet = err.stack.split('\n').slice(0, 3).join('\n');
      }
    }
    if (!isEmptyInfo(context)) info = context;
  }

  // La versione app va sempre allegata, anche senza altro contesto, per
  // correlare l'errore alla build che l'ha prodotto.
  const scrubbed = info === undefined ? undefined : scrubSensitiveValue(info);
  if (scrubbed && typeof scrubbed === 'object' && !Array.isArray(scrubbed)) {
    return { ...scrubbed, appVersion: APP_VERSION };
  }
  return scrubbed === undefined
    ? { appVersion: APP_VERSION }
    : { detail: scrubbed, appVersion: APP_VERSION };
}

// Lightweight client-side throttling to avoid flooding log_errors with
// duplicate rows from a single underlying failure (e.g. JWT expiry storms).
const DEDUP_WINDOW_MS = 30_000;
const recentLogKeys = new Map<string, number>();

function isDuplicateWithinWindow(key: string): boolean {
  const now = Date.now();
  const last = recentLogKeys.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
    return true;
  }
  recentLogKeys.set(key, now);
  if (recentLogKeys.size > 200) {
    for (const [k, t] of recentLogKeys) {
      if (now - t > DEDUP_WINDOW_MS) recentLogKeys.delete(k);
    }
  }
  return false;
}

/**
 * Sanitize log message to remove sensitive information
 */
function sanitizeLogMessage(message: any): any {
  if (typeof message === 'string') {
    let sanitized = message;
    
    // In production, sanitize sensitive information
    if (!isDev) {
      SENSITIVE_KEYWORDS.forEach(keyword => {
        const regex = new RegExp(`${keyword}[=:][^\\s,}]+`, 'gi');
        sanitized = sanitized.replace(regex, `${keyword}=***`);
      });
    }
    
    return sanitized;
  }
  
  if (typeof message === 'object' && message !== null) {
    if (!isDev) {
      try {
        const sanitized = { ...message };
        
        // Remove sensitive properties
        SENSITIVE_KEYWORDS.forEach(keyword => {
          if (sanitized.hasOwnProperty(keyword)) {
            sanitized[keyword] = '***';
          }
        });
        
        return sanitized;
      } catch {
        return '[OBJECT_SANITIZED]';
      }
    }
  }
  
  return message;
}

/**
 * Format log message with timestamp and sanitization
 */
const formatMessage = (level: LogLevel, message: any, ...optional: any[]) => {
  const timestamp = new Date().toISOString();
  const sanitizedMessage = sanitizeLogMessage(message);
  const sanitizedOptional = optional.map(sanitizeLogMessage);
  
  return [`[${timestamp}] [${level.toUpperCase()}]:`, sanitizedMessage, ...sanitizedOptional];
};

/**
 * Check if message contains sensitive information that should be filtered
 */
function containsSensitiveInfo(message: any): boolean {
  if (!isDev && typeof message === 'string') {
    return SENSITIVE_KEYWORDS.some(keyword => 
      message.toLowerCase().includes(keyword.toLowerCase())
    );
  }
  return false;
}

/**
 * Forward an error to Sentry with a fingerprint that splits it by page +
 * error_code. Without this, every event is `new Error(...)` created at the same
 * line here, so Sentry's stack-based grouping collapses unrelated failures into
 * one bucket (CERALIA-90, #1479). The fingerprint groups by real cause instead.
 */
function reportToSentry(
  diagnosticMessage: string,
  page: string,
  errorCode: string | null
): void {
  Sentry.captureException(new Error(diagnosticMessage), {
    fingerprint: ['logError', page, errorCode ?? diagnosticMessage]
  });
}

const logger = {
  log: (message: any, ...optional: any[]) => {
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.log && !containsSensitiveInfo(message)) {
      console.log(...formatMessage('log', message, ...optional));
    }
  },
  
  info: (message: any, ...optional: any[]) => {
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.info) {
      console.info(...formatMessage('info', message, ...optional));
    }
  },
  
  warn: (message: any, ...optional: any[]) => {
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.warn) {
      console.warn(...formatMessage('warn', message, ...optional));
    }
  },
  
  error: (message: any, ...optional: any[]) => {
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.error) {
      // Sanitize error messages in production
      const sanitizedMessage = isDev ? message : sanitizeErrorMessage(message);
      console.error(...formatMessage('error', sanitizedMessage, ...optional));
    }
  },
  
  debug: (message: any, ...optional: any[]) => {
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.debug && !containsSensitiveInfo(message)) {
      console.debug(...formatMessage('debug', message, ...optional));
    }
  },
  
  // Special method for security-related logging
  security: (event: string, details?: any) => {
    if (LOG_ENABLED) {
      const sanitizedDetails = sanitizeLogMessage(details);
      console.warn(...formatMessage('warn', `SECURITY_EVENT: ${event}`, sanitizedDetails));
    }
  },

  /**
   * Centralized error logging to Supabase and Sentry
   */
  logError: async (
    page: string,
    errorMessage: string,
    options?: { errorCode?: string; additionalInfo?: any; error?: unknown }
  ) => {
    const normalized =
      options?.error !== undefined ? normalizeError(options.error) : null;

    // The user-facing message (toasts) is sanitized to a generic string elsewhere.
    // If that generic string reaches us as `errorMessage`, persisting it would
    // collapse unrelated failures into a single, unactionable Sentry bucket
    // (CERALIA-90, #1479). When the caller still has the original error, derive
    // the REAL diagnostic message from it; otherwise keep what we were given.
    const realMessage =
      isGenericUserMessage(errorMessage) && normalized && !isGenericUserMessage(normalized.message)
        ? normalized.message
        : errorMessage;

    // Persist the real diagnostic message, only scrubbed of secrets.
    const diagnosticMessage = scrubSensitiveString(realMessage);

    if (isNetworkErrorMessage(realMessage)) {
      console.warn('[logger] Skipping remote logging for network/offline error', {
        page,
        error: diagnosticMessage,
        errorCode: options?.errorCode
      });
      return;
    }

    const resolvedErrorCode =
      options?.errorCode ??
      normalized?.code ??
      (typeof normalized?.status === 'number' ? String(normalized.status) : undefined) ??
      null;

    const resolvedAdditionalInfo = buildAdditionalInfo(options?.additionalInfo, normalized);

    // Drop duplicate rows from the same underlying failure within a short window.
    const dedupKey = `${page}|${resolvedErrorCode ?? ''}|${diagnosticMessage}`;
    if (isDuplicateWithinWindow(dedupKey)) {
      return;
    }

    let shouldReportToSentry = true;

    try {
      const userId = await getCachedUserId();
      const { supabase } = await import('@/integrations/supabase/client');

      const buildPayload = (overrideUserId?: string | null) => ({
        user_id:
          overrideUserId === undefined
            ? userId ?? null
            : overrideUserId,
        page,
        error_code: resolvedErrorCode,
        error_message: diagnosticMessage,
        additional_info: resolvedAdditionalInfo
      });

      // supabase-js segnala gli errori PostgREST (RLS, FK, ...) risolvendo con
      // { error } e fa reject solo sui fallimenti di rete. Normalizziamo i due
      // casi in un unico valore-errore così la logica sotto non dipende dalla
      // forma con cui l'errore arriva.
      const runInsert = async (overrideUserId?: string | null): Promise<unknown> => {
        try {
          const { error } = await supabase.from('log_errors').insert(buildPayload(overrideUserId));
          return error ?? null;
        } catch (err) {
          return err;
        }
      };

      // Utenti non autenticati: il ruolo anon non ha alcun privilegio su
      // log_errors (REVOKE ALL FROM anon, migration 20260611120000). Tentare
      // l'INSERT produrrebbe solo un "permission denied" ricorrente (Sentry
      // CERALIA-98, #1442) senza mai persistere nulla. Saltiamo la scrittura
      // remota: l'errore originale viene comunque inoltrato a Sentry sotto.
      if (userId) {
        const insertErr = await runInsert(userId);

        if (insertErr) {
          console.error('[logger] Failed to persist error log', insertErr);

          if (isUserForeignKeyConstraintError(insertErr)) {
            // L'utente referenziato non esiste più: ritentiamo in forma anonima
            // (user_id = NULL), ora ammessa dalla policy RLS corretta in
            // 20260612090000 (#1442), così l'errore non va perso.
            console.warn('[logger] Retrying error log insert without user_id due to FK constraint', {
              page,
              userId
            });

            const fallbackErr = await runInsert(null);

            if (!fallbackErr) {
              console.info('[logger] Error log persisted using anonymous fallback', { page });
              shouldReportToSentry = false;
            } else {
              console.error('[logger] Fallback insert without user_id failed', fallbackErr);
              if (isNetworkErrorMessage(fallbackErr)) {
                shouldReportToSentry = false;
              }
            }
          } else if (isNetworkErrorMessage(insertErr)) {
            shouldReportToSentry = false;
          }
        }
      }

      if (!isDev && shouldReportToSentry) {
        reportToSentry(diagnosticMessage, page, resolvedErrorCode);
      }
    } catch (err) {
      console.error('[logger] Failed to log error', err);
      if (!isDev && !isNetworkErrorMessage(err)) {
        reportToSentry(diagnosticMessage, page, resolvedErrorCode);
      }
    }
  }
};

export default logger;
